import { useState } from 'react'
import { Trash2 } from 'lucide-react'
import type { NtCourse, NtTerm } from '../../core/types'
import { useAwards, saveAward, removeAward } from './model'
import { Text, Select, Area } from '../../ui/Field'
import { Empty, ErrorState } from '../../ui/States'

/**
 * Open-house awards.
 *
 * In the prototype students voted. Without student sign-in there is nobody to
 * vote, so this is the half that works today: recording who won, so it survives
 * into the term's history instead of living on a whiteboard until somebody
 * wipes it.
 *
 * When student accounts arrive, the voting screen writes into these same
 * records — this is not a placeholder that gets thrown away.
 */
export function Awards({ term, courses, canRecord, who }: {
  term: NtTerm
  courses: NtCourse[]
  canRecord: boolean
  who: string
}) {
  const awards = useAwards(term.id)
  const [adding, setAdding] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  return (
    <>
      {err && <ErrorState body={err} />}

      {!canRecord && awards.length === 0 && (
        <Empty title="No awards yet" body="They go up here after the open house." />
      )}

      {canRecord && (
        adding
          ? <AwardForm term={term} courses={courses} who={who} onDone={() => setAdding(false)} />
          : <button className="btn" style={{ marginTop: 'var(--s-2)' }} onClick={() => setAdding(true)}>
              Record an award
            </button>
      )}

      {awards.length > 0 && (
        <ul className="work" style={{ marginTop: 'var(--s-4)' }}>
          {awards.map((a) => (
            <li key={a.id} className="work__row">
              <div className="work__main">
                <div style={{ fontWeight: 610 }}>{a.category}</div>
                <div className="work__sub">{a.courseTitle || a.studentName || '—'}</div>
                {a.note && <div className="work__status">{a.note}</div>}
              </div>
              {canRecord && (
                <button className="opt__icon opt__icon--danger" aria-label={`Remove ${a.category}`}
                  onClick={() => removeAward(a.id).catch((e) => setErr(String(e)))}>
                  <Trash2 size={15} aria-hidden="true" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <p className="muted-note">
        Student voting arrives with student sign-in. Until then these are recorded by the office,
        and the voting screen will write into these same records rather than replacing them.
      </p>
    </>
  )
}

function AwardForm({ term, courses, who, onDone }: {
  term: NtTerm; courses: NtCourse[]; who: string; onDone: () => void
}) {
  const approved = courses.filter((c) => c.status === 'approved')
  const [f, setF] = useState({
    category: '', courseTitle: approved[0]?.title ?? '', studentName: '', note: '',
  })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const save = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true); setErr(null)
    const course = approved.find((c) => c.title === f.courseTitle)
    try {
      await saveAward({
        termId: term.id,
        category: f.category.trim(),
        ...(course ? { courseId: course.id, courseTitle: course.title } : {}),
        ...(f.studentName.trim() ? { studentName: f.studentName.trim() } : {}),
        note: f.note.trim(),
      }, who)
      onDone()
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); setBusy(false) }
  }

  return (
    <form className="card card--action" onSubmit={save} style={{ marginTop: 'var(--s-2)' }}>
      <h3 style={{ fontSize: 'var(--t-md)', margin: '0 0 var(--s-3)' }}>Record an award</h3>
      {err && <ErrorState body={err} />}
      <Text label="Award" value={f.category} onChange={(v) => setF((p) => ({ ...p, category: v }))}
        required placeholder="Best Demonstration" />
      <Select label="Course" value={f.courseTitle} onChange={(v) => setF((p) => ({ ...p, courseTitle: v }))}
        options={approved.map((c) => c.title)} allowOther
        help="Or type a student's name below if it went to an independent project." />
      <Text label="Student (independent projects)" value={f.studentName}
        onChange={(v) => setF((p) => ({ ...p, studentName: v }))} />
      <Area label="Why" value={f.note} onChange={(v) => setF((p) => ({ ...p, note: v }))} />
      <div className="opt__bar">
        <button className="btn" type="submit" disabled={busy || !f.category.trim()}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button className="btn btn--ghost" type="button" onClick={onDone} disabled={busy}>Cancel</button>
      </div>
    </form>
  )
}
